import { useEffect, useState } from "react";
import { Clapperboard, Wand2, Film, Radio, Coins, Gift, Users, Lightbulb, ChevronDown, Bot, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Page, PageHead, inputCls } from "@/components/hub/Common";
import { openAssist } from "@/components/assist/assistApi";

const ICONS = { clapperboard: Clapperboard, wand: Wand2, film: Film, radio: Radio, coins: Coins, gift: Gift, users: Users };

const Section = ({ s, i }) => {
  const Icon = ICONS[s.icon] || Lightbulb;
  return (
    <section id={`guide-${s.id}`} data-testid={`guide-section-${s.id}`} className="cx-card rounded-2xl p-6 sm:p-8 scroll-mt-24 cx-rise" style={{ animationDelay: `${i * 60}ms` }}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0" style={{ background: `${s.color}22`, boxShadow: `0 0 24px ${s.color}33` }}>
          <Icon className="w-6 h-6" style={{ color: s.color }} />
        </div>
        <div className="min-w-0">
          <div className="cx-label" style={{ color: s.color }}>{String(i + 1).padStart(2, "0")}</div>
          <h2 className="font-display text-xl sm:text-2xl text-white mt-1">{s.title}</h2>
          <p className="text-sm text-slate-400 mt-2">{s.intro}</p>
        </div>
      </div>
      <ol className="mt-6 space-y-3">
        {s.steps.map((st, k) => (
          <li key={k} className="flex gap-3 text-sm text-slate-300">
            <span className="w-6 h-6 rounded-md text-[11px] font-mono grid place-items-center shrink-0 border" style={{ borderColor: `${s.color}55`, color: s.color }}>{k + 1}</span>
            <span className="pt-0.5">{st}</span>
          </li>
        ))}
      </ol>
      {s.tips.length > 0 && (
        <div className="mt-6 rounded-xl border border-white/5 bg-white/[0.02] p-4 space-y-2">
          {s.tips.map((tp, k) => <p key={k} className="flex gap-2 text-xs text-slate-400"><Lightbulb className="w-3.5 h-3.5 text-[#ffc94d] shrink-0 mt-0.5" />{tp}</p>)}
        </div>
      )}
    </section>
  );
};

const Faq = ({ f, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="cx-card rounded-xl" data-testid={`guide-faq-${i}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`guide-faq-toggle-${i}`} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="text-sm text-white">{f.q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-4 text-sm text-slate-400 leading-relaxed" data-testid={`guide-faq-answer-${i}`}>{f.a}</p>}
    </div>
  );
};

export default function Guide() {
  const { t, lang } = useI18n();
  const [g, setG] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/guide").then((r) => setG(r.data)); }, [lang]);
  const match = (o) => !q.trim() || JSON.stringify(o).toLowerCase().includes(q.trim().toLowerCase());
  const sections = (g?.sections || []).filter(match);
  const faq = (g?.faq || []).filter(match);
  return (
    <Page>
      <PageHead label={t("gd.label")} t1={t("gd.title1")} t2={t("gd.title2")} sub={t("gd.sub")}
        right={<button onClick={() => openAssist()} data-testid="guide-ask-ai-btn" className="cx-btn rounded-full px-5 py-3 text-sm flex items-center gap-2"><Bot className="w-4 h-4" />{t("gd.ask")}</button>} />
      <div className="relative mt-8 max-w-xl">
        <Search className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("gd.search")} data-testid="guide-search-input" className={`${inputCls} pl-11`} />
      </div>
      <div className="grid lg:grid-cols-[220px_1fr] gap-8 mt-10 items-start">
        <nav className="hidden lg:flex flex-col gap-1 sticky top-6" data-testid="guide-toc">
          {(g?.sections || []).map((s) => (
            <a key={s.id} href={`#guide-${s.id}`} data-testid={`guide-toc-${s.id}`} className="text-sm text-slate-400 hover:text-white px-3 py-2 rounded-lg hover:bg-white/[0.03] transition-colors">{s.title}</a>
          ))}
          <a href="#guide-faq" data-testid="guide-toc-faq" className="text-sm text-slate-400 hover:text-white px-3 py-2 rounded-lg hover:bg-white/[0.03]">{t("gd.faq")}</a>
        </nav>
        <div className="space-y-6 min-w-0">
          {sections.map((s, i) => <Section key={s.id} s={s} i={i} />)}
          <div id="guide-faq" className="scroll-mt-24 pt-4">
            <h2 className="font-display text-xl sm:text-2xl text-white mb-4">{t("gd.faq")}</h2>
            <div className="space-y-3">{faq.map((f, i) => <Faq key={f.q} f={f} i={i} />)}</div>
          </div>
          {g && !sections.length && !faq.length && <p className="text-sm text-slate-500" data-testid="guide-no-result">{t("gd.none")}</p>}
          <div className="cx-card rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-[#a746ff]/30" data-testid="guide-cta">
            <div><div className="text-white font-display">{t("gd.cta_t")}</div><p className="text-sm text-slate-400 mt-1">{t("gd.cta_d")}</p></div>
            <button onClick={() => openAssist()} data-testid="guide-cta-ask-btn" className="cx-btn rounded-full px-5 py-3 text-sm flex items-center gap-2 shrink-0"><Bot className="w-4 h-4" />{t("gd.ask")}</button>
          </div>
        </div>
      </div>
    </Page>
  );
}
